import { SupabaseClient, PostgrestError } from '@supabase/supabase-js';
import { Database } from '../../types/supabase';
import {
  Mission,
  UserMission,
  MissionActionRequirement,
  UserMissionProgress,
  XpActionKey,
  MissionRow,
  UserMissionRow,
  UserMissionUpdateInfo,
  MissionRequiredActions,
  SpecialEventRow,
  XPMultiplierEventConfig,
  SpecialEvent,
  SpecialEventConfig
} from './types';
import { XpHistoryDetails } from './types';
// import { addUserXP, awardBadgeToUser } from './gamificationService'; // Descomentar quando necessário

type Supabase = SupabaseClient<Database>;

const MISSION_TABLE = 'missions';
const USER_MISSION_TABLE = 'user_missions';
const SPECIAL_EVENTS_TABLE = 'special_events'; // Adicionado para getActiveEventMultiplier

/**
 * Busca um modelo de missão pela sua chave única (mission_key).
 */
export async function getMissionTemplateByKey(supabase: Supabase, missionKey: string): Promise<Mission | null> {
  const { data, error } = await supabase
    .from(MISSION_TABLE)
    .select('*')
    .eq('mission_key', missionKey)
    .maybeSingle();

  if (error) {
    console.error('[MissionService:getMissionTemplateByKey] Error fetching mission template:', error);
    return null;
  }
  // Precisamos garantir que required_actions seja do tipo correto, mesmo vindo como JSONB do DB
  return data ? { ...data, required_actions: data.required_actions as unknown as MissionRequiredActions } as Mission : null;
}

/**
 * Lista todos os modelos de missão, com filtros opcionais.
 */
export async function listMissionTemplates(
  supabase: Supabase,
  filters: Partial<Pick<MissionRow, 'mission_type' | 'is_active' | 'frequency'>> = {}
): Promise<Mission[]> {
  let query = supabase.from(MISSION_TABLE).select('*');

  if (filters.mission_type) {
    query = query.eq('mission_type', filters.mission_type);
  }
  if (filters.is_active !== undefined) {
    query = query.eq('is_active', filters.is_active);
  }
  if (filters.frequency) {
    query = query.eq('frequency', filters.frequency);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[MissionService:listMissionTemplates] Error fetching mission templates:', error);
    return [];
  }
  return data ? data.map(m => ({ ...m, required_actions: m.required_actions as unknown as MissionRequiredActions } as Mission)) : [];
}

/**
 * Cria um novo modelo de missão.
 */
export async function createMissionTemplate(
  supabase: Supabase,
  missionData: Omit<MissionRow, 'id' | 'created_at' | 'updated_at'>
): Promise<Mission | null> {
  const { data, error } = await supabase
    .from(MISSION_TABLE)
    .insert(missionData)
    .select()
    .single();

  if (error) {
    console.error('[MissionService:createMissionTemplate] Error creating mission template:', error);
    return null;
  }
  return data ? { ...data, required_actions: data.required_actions as unknown as MissionRequiredActions } as Mission : null;
}

/**
 * Atualiza um modelo de missão existente.
 */
export async function updateMissionTemplate(
  supabase: Supabase,
  missionId: string,
  missionData: Partial<Omit<MissionRow, 'id'|'created_at'|'updated_at'>>
): Promise<Mission | null> {
  const { data, error } = await supabase
    .from(MISSION_TABLE)
    .update(missionData)
    .eq('id', missionId)
    .select()
    .single();

  if (error) {
    console.error('[MissionService:updateMissionTemplate] Error updating mission template:', error);
    return null;
  }
  return data ? { ...data, required_actions: data.required_actions as unknown as MissionRequiredActions } as Mission : null;
}


/**
 * Busca missões ativas (não completas e não expiradas) para um usuário.
 * Opcionalmente pode filtrar por tipo de missão.
 */
export async function getUserActiveMissions(
  supabase: Supabase,
  userId: string,
  type?: 'daily' | 'weekly' | 'event'
): Promise<UserMission[]> {
  let query = supabase
    .from(USER_MISSION_TABLE)
    .select(
      '*, missions(*)'
    )
    .eq('user_id', userId)
    .eq('is_complete', false)
    .or(`expires_at.is.null,expires_at.gt.${new Date().toISOString()}`);

  if (type && type !== 'event') { // Para 'event', o filtro pode ser mais complexo ou vir do mission_key
    // Se quisermos filtrar UserMission pelo mission_type do Mission template, precisaremos de um join ou subquery mais complexa
    // Ou filtrar após buscar, se o volume não for muito grande.
    // Por ora, este filtro de tipo é mais um placeholder para desenvolvimento futuro se a query for ajustada.
    // Para filtrar por mission_type diretamente na query, a coluna 'mission_type' teria que estar em 'user_missions'
    // ou faríamos um RPC ou um select mais elaborado.
    // query = query.eq('missions.mission_type', type); // Isso não funciona diretamente assim com o select aninhado
  }


  const { data, error } = await query;

  if (error) {
    console.error('[MissionService:getUserActiveMissions] Error fetching user active missions:', error);
    return [];
  }

  return data ? data.map(um => ({
    ...um,
    progress: um.progress as unknown as UserMissionProgress,
    mission: um.missions ? { ...um.missions, required_actions: um.missions.required_actions as unknown as MissionRequiredActions } as Mission : undefined,
  })) as UserMission[] : [];
}

/**
 * Atribui uma missão específica (modelo) a um usuário.
 */
export async function assignMissionToUser(
  supabase: Supabase,
  userId: string,
  missionId: string, // ID do Mission template
  expiresAt?: Date | null // Null para missões que não expiram (ex: algumas de evento)
): Promise<UserMission | null> {
  // Verificar se já existe uma instância não completa e não expirada desta missão para este usuário
  // Esta verificação pode ser mais complexa dependendo da constraint UNIQUE em user_missions
  // Se a constraint for (user_id, mission_id, assigned_at), então sempre podemos criar uma nova.
  // Se a constraint for para evitar duplicação de uma missão *ativa*, precisamos de uma query.
  
  // Exemplo de verificação:
  // const { data: existing, error: existingError } = await supabase
  //   .from(USER_MISSION_TABLE)
  //   .select('id')
  //   .eq('user_id', userId)
  //   .eq('mission_id', missionId)
  //   .eq('is_complete', false)
  //   .or(\`expires_at.is.null,expires_at.gt.${new Date().toISOString()}\`)
  //   .maybeSingle();

  // if (existingError) {
  //   console.error('[MissionService:assignMissionToUser] Error checking existing mission:', existingError);
  //   return null;
  // }
  // if (existing) {
  //   console.warn(\`[MissionService:assignMissionToUser] User \${userId} already has an active instance of mission \${missionId}.\`);
  //   return null; // Ou retornar a missão existente
  // }

  const insertData: Omit<UserMissionRow, 'id' | 'created_at' | 'updated_at' | 'progress' | 'completed_at' | 'is_complete' | 'is_rewarded'> & { expires_at?: string | null } = {
    user_id: userId,
    mission_id: missionId,
    assigned_at: new Date().toISOString(),
    expires_at: expiresAt ? expiresAt.toISOString() : null,
  };

  const { data, error } = await supabase
    .from(USER_MISSION_TABLE)
    .insert(insertData)
    .select(
      '*, missions(*)'
    )
    .single();

  if (error) {
    console.error('[MissionService:assignMissionToUser] Error assigning mission to user:', error);
    return null;
  }
  return data ? {
    ...data,
    progress: data.progress as unknown as UserMissionProgress,
    mission: data.missions ? { ...data.missions, required_actions: data.missions.required_actions as unknown as MissionRequiredActions } as Mission : undefined,
  } as UserMission : null;
}


// --- Funções de Gerenciamento de Missões Diárias/Semanais (esqueleto) ---

const DAILY_MISSIONS_TO_ASSIGN = 2; // Quantas missões diárias atribuir
const WEEKLY_MISSIONS_TO_ASSIGN = 2; // Quantas missões semanais atribuir

/**
 * Garante que o usuário tenha o conjunto correto de missões diárias.
 * A ser chamado no login do usuário ou por um job.
 * Retorna o número de missões diárias recém-atribuídas.
 */
export async function ensureDailyMissionsForUser(supabase: Supabase, userId: string): Promise<number> {
  const today = new Date();
  const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
  const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);

  let assignedCount = 0;
  try {
    const userMissions = await getUserActiveMissions(supabase, userId);
    const currentDailyMissionIds = userMissions
      .filter(um => um.mission?.frequency === 'daily' && um.expires_at && new Date(um.expires_at) >= startOfDay && new Date(um.expires_at) <= endOfDay)
      .map(um => um.mission_id).filter(id => id !== undefined) as string[];

    if (currentDailyMissionIds.length >= DAILY_MISSIONS_TO_ASSIGN) {
      return 0;
    }

    const neededCount = DAILY_MISSIONS_TO_ASSIGN - currentDailyMissionIds.length;
    if (neededCount <= 0) return 0;

    const missionTemplates = await listMissionTemplates(supabase, { frequency: 'daily', is_active: true });
    const assignableTemplates = missionTemplates.filter(mt => mt.mission_type === 'daily' && !currentDailyMissionIds.includes(mt.id));

    const selectedTemplates = assignableTemplates
      .sort(() => 0.5 - Math.random())
      .slice(0, neededCount);

    for (const template of selectedTemplates) {
      const assigned = await assignMissionToUser(supabase, userId, template.id, endOfDay);
      if (assigned) {
        assignedCount++;
      }
    }
  } catch (error) {
    console.error('[MissionService:ensureDailyMissionsForUser] Error:', error);
    // Retornar 0 em caso de erro para não impedir o fluxo principal
    return 0; 
  }
  return assignedCount;
}

/**
 * Garante que o usuário tenha o conjunto correto de missões semanais.
 * A ser chamado no início da semana para o usuário ou por um job.
 * Retorna o número de missões semanais recém-atribuídas.
 */
export async function ensureWeeklyMissionsForUser(supabase: Supabase, userId: string): Promise<number> {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 (Dom) - 6 (Sab)
  const startOfWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1), 0, 0, 0, 0);
  const endOfWeek = new Date(startOfWeek.getFullYear(), startOfWeek.getMonth(), startOfWeek.getDate() + 6, 23, 59, 59, 999);
  
  let assignedCount = 0;
  try {
    const userMissions = await getUserActiveMissions(supabase, userId);
    const currentWeeklyMissionIds = userMissions
      .filter(um => um.mission?.frequency === 'weekly' && um.expires_at && new Date(um.expires_at) >= startOfWeek && new Date(um.expires_at) <= endOfWeek)
      .map(um => um.mission_id).filter(id => id !== undefined) as string[];

    if (currentWeeklyMissionIds.length >= WEEKLY_MISSIONS_TO_ASSIGN) {
      return 0;
    }

    const neededCount = WEEKLY_MISSIONS_TO_ASSIGN - currentWeeklyMissionIds.length;
    if (neededCount <= 0) return 0;

    const missionTemplates = await listMissionTemplates(supabase, { frequency: 'weekly', is_active: true });
    const assignableTemplates = missionTemplates.filter(mt => mt.mission_type === 'weekly' && !currentWeeklyMissionIds.includes(mt.id));

    const selectedTemplates = assignableTemplates
      .sort(() => 0.5 - Math.random())
      .slice(0, neededCount);

    for (const template of selectedTemplates) {
      const assigned = await assignMissionToUser(supabase, userId, template.id, endOfWeek);
      if (assigned) {
        assignedCount++;
      }
    }
  } catch (error) {
    console.error('[MissionService:ensureWeeklyMissionsForUser] Error:', error);
    return 0;
  }
  return assignedCount;
}

/**
 * Gera e atribui missões diárias e semanais para um usuário, se necessário.
 * Retorna o número de missões de cada tipo que foram recém-atribuídas.
 */
export async function generateUserMissions(supabase: Supabase, userId: string): Promise<{ dailyGenerated: number; weeklyGenerated: number }> {
  let dailyGenerated = 0;
  let weeklyGenerated = 0;
  try {
    dailyGenerated = await ensureDailyMissionsForUser(supabase, userId);
  } catch (e) { 
    console.error("[MissionService:generateUserMissions] Error in ensureDailyMissionsForUser:", e); 
  }
  try {
    weeklyGenerated = await ensureWeeklyMissionsForUser(supabase, userId);
  } catch (e) { 
    console.error("[MissionService:generateUserMissions] Error in ensureWeeklyMissionsForUser:", e); 
  }
  return { dailyGenerated, weeklyGenerated };
}


// --- Lógica de Progresso e Conclusão (esqueleto) ---
// Comentários originais e estrutura da função processUserActionForMissions devem ser restaurados aqui
// Se a função em si foi comentada anteriormente, essa estrutura de comentário deve ser mantida.
// Por exemplo, se antes era:
// /**
//  * Processa uma ação do usuário...
//  */
// export async function processUserActionForMissions(...) { ... }
// E foi comentada para:
// /*
//  * Processa uma ação do usuário...
//  */
// export async function processUserActionForMissions(...) { ... }
// */
// Então essa estrutura comentada deve ser mantida.
// Vamos assumir por agora que a função estava implementada e apenas a sua chamada no gamificationService estava comentada.

/**
 * Processa uma ação do usuário, verifica missões relevantes, atualiza progresso e concede recompensas.
 */
export async function processUserActionForMissions(
  supabase: Supabase,
  userId: string,
  actionTypePerformed: XpActionKey | string,
  actionData: any = {} // actionData DEVE conter sourceId se a missão requer unicidade, e category se requer categoria.
                       // Ex: { sourceId: 'entity123', category: 'some_category' }
  // gamificationService: any 
): Promise<UserMissionUpdateInfo[]> {
  const updates: UserMissionUpdateInfo[] = [];
  
  try {
    const activeUserMissions = await getUserActiveMissions(supabase, userId);

    if (!activeUserMissions || activeUserMissions.length === 0) {
      return [];
    }

    for (const userMission of activeUserMissions) {
      if (!userMission.mission || !userMission.mission.required_actions) {
        continue;
      }

      const missionTemplate = userMission.mission;
      let missionProgressChanged = false;
      // Garantir que newProgress seja inicializado corretamente como UserMissionProgress
      const newProgress: UserMissionProgress = JSON.parse(JSON.stringify(userMission.progress as unknown as UserMissionProgress || {})); 

      for (const reqAction of missionTemplate.required_actions) {
        if (reqAction.action_type === actionTypePerformed) {
          let passesParamChecks = true;
          const actionProgressKey = reqAction.action_type;

          // Inicializar o progresso para esta ação se não existir
          if (!newProgress[actionProgressKey]) {
            newProgress[actionProgressKey] = { count: 0, processedEntities: [] };
          }
          // Garantir que processedEntities exista se for uma ação de unicidade
          if (reqAction.params?.unique_entity && !newProgress[actionProgressKey].processedEntities) {
            newProgress[actionProgressKey].processedEntities = [];
          }

          const currentActionDetails = newProgress[actionProgressKey];

          // 1. Verificar filtro de categoria (se houver)
          if (reqAction.params?.category) {
            if (!actionData.category || actionData.category !== reqAction.params.category) {
              passesParamChecks = false;
            }
          }

          // 2. Verificar unicidade da entidade (se requerido e aplicável)
          if (passesParamChecks && reqAction.params?.unique_entity) {
            if (!actionData.sourceId) {
              console.warn(`[MissionService] Ação ${actionTypePerformed} para missão ${missionTemplate.id} requer unique_entity mas actionData.sourceId não foi fornecido.`);
              passesParamChecks = false;
            } else if (currentActionDetails.processedEntities?.includes(actionData.sourceId)) {
              // Entidade já processada para esta missão/ação
              passesParamChecks = false; 
            }
          }

          if (passesParamChecks) {
            if (currentActionDetails.count < reqAction.quantity) {
              currentActionDetails.count += 1;
              missionProgressChanged = true;

              if (reqAction.params?.unique_entity && actionData.sourceId) {
                // Adicionar à lista de processados, mesmo que já exista (para garantir)
                currentActionDetails.processedEntities = [
                  ...(currentActionDetails.processedEntities || []),
                  actionData.sourceId,
                ];
              }
            }
          } // fim passesParamChecks
        } // fim action_type === actionTypePerformed
      } // fim loop reqAction

      if (missionProgressChanged) {
        const { error: updateError } = await supabase
          .from(USER_MISSION_TABLE)
          .update({ progress: newProgress as any })
          .eq('id', userMission.id);

        if (updateError) {
          console.error(`[MissionService:processUserActionForMissions] Error updating progress for user_mission ${userMission.id}:`, updateError);
          continue;
        }
        
        userMission.progress = newProgress; // Atualizar objeto local

        let isMissionComplete = true;
        for (const reqAction of missionTemplate.required_actions) {
          const progressDetails = newProgress[reqAction.action_type];
          if (!progressDetails || progressDetails.count < reqAction.quantity) {
            isMissionComplete = false;
            break;
          }
        }

        if (isMissionComplete) {
          const { error: completeError } = await supabase
            .from(USER_MISSION_TABLE)
            .update({ 
              is_complete: true, 
              completed_at: new Date().toISOString(),
              progress: newProgress as any 
            })
            .eq('id', userMission.id);

          // Log: Verificar se a missão foi completada e os valores de recompensa ANTES de adicionar ao array.
          console.log(`[MissionService:processUserActionForMissions] Missão ${userMission.id} (template: ${missionTemplate.id}) está completa. XP Reward: ${missionTemplate.xp_reward}, Badge Reward ID: ${missionTemplate.badge_reward_id}`);

          if (completeError) {
            console.error(`[MissionService:processUserActionForMissions] Error marking mission ${userMission.id} complete:`, completeError);
          } else {
            // Missão completada, preencher detalhes da recompensa para o array de updates
            const updateInfo: UserMissionUpdateInfo = {
              missionId: userMission.id, // Usar userMission.id para rastreamento específico
              missionTitle: missionTemplate.title,
              status: 'completed',
              xp_reward_granted: missionTemplate.xp_reward > 0 ? missionTemplate.xp_reward : undefined,
              badge_reward_granted: missionTemplate.badge_reward_id || undefined,
            };
            // Log: Verificar o objeto updateInfo ANTES de dar push
            console.log(`[MissionService:processUserActionForMissions] updateInfo a ser adicionado:`, JSON.stringify(updateInfo, null, 2));
            updates.push(updateInfo);
          }
        } else {
          updates.push({
            missionId: userMission.id, // Usar userMission.id aqui também para consistência
            missionTitle: missionTemplate.title,
            status: 'progress',
          });
        }
      }
    }
  } catch (error) {
    console.error('[MissionService:processUserActionForMissions] General error:', error);
    // Em caso de erro geral, explicitamente retornamos um array vazio para evitar comportamento indefinido.
    // Isso também garante que, se houver um erro não capturado antes, não retornemos um 'updates' potencialmente incompleto.
    return []; 
  }

  // Log final ANTES de retornar o array 'updates'
  console.log('[MissionService:processUserActionForMissions] Array updates ANTES de criar finalReturnValue:', JSON.stringify(updates, null, 2));
  
  // Criar uma cópia profunda (superficial dos objetos internos é suficiente aqui)
  const finalReturnValue = updates.map(item => ({ ...item })); 

  // Adicionar log para o primeiro item ANTES de retornar, se existir
  if (finalReturnValue && finalReturnValue.length > 0) {
    console.log('[MissionService:processUserActionForMissions] ITEM 0 ANTES DE RETORNAR:', JSON.stringify(finalReturnValue[0]));
  }

  console.log('[MissionService:processUserActionForMissions] Retornando finalReturnValue (APÓS CÓPIA PROFUNDA DOS ITENS):', JSON.stringify(finalReturnValue, null, 2));
  return finalReturnValue;
}

// --- NOVA FUNÇÃO: getActiveEventMultiplier ---
/**
 * Busca o multiplicador de XP de um evento especial ativo para uma determinada ação e usuário.
 * Retorna 1 se nenhum evento aplicável for encontrado.
 */
export async function getActiveEventMultiplier(
  supabase: Supabase,
  actionKey: XpActionKey | string,
  userId?: string // userId pode ser usado para eventos direcionados
): Promise<number> {
  const now = new Date().toISOString();
  try {
    const { data: activeEvents, error } = await supabase
      .from(SPECIAL_EVENTS_TABLE)
      .select('*')
      .eq('event_type', 'XP_MULTIPLIER')
      .eq('is_active', true)
      .lte('start_date', now)
      .gte('end_date', now);

    if (error) {
      console.error('[MissionService:getActiveEventMultiplier] Error fetching active XP multiplier events:', error);
      return 1;
    }

    if (!activeEvents || activeEvents.length === 0) {
      return 1;
    }

    let highestMultiplier = 1;

    for (const event of activeEvents) {
      const config = event.config as unknown as XPMultiplierEventConfig; 
      // Fazendo um cast mais permissivo para target_user_ids devido a problemas de inferência
      const targetUserIds = (event as any).target_user_ids as string[] | null;

      // Lógica para eventos direcionados
      if (userId && targetUserIds && targetUserIds.length > 0 && !targetUserIds.includes(userId)) {
        continue; // Evento não é para este usuário
      }

      if (config && typeof config.xp_multiplier === 'number' && Array.isArray(config.affected_actions)) {
        if (config.affected_actions.includes('all') || config.affected_actions.includes(actionKey as string)) {
          if (config.xp_multiplier > highestMultiplier) {
            highestMultiplier = config.xp_multiplier;
          }
        }
      }
    }
    return highestMultiplier;
  } catch (e) {
    console.error('[MissionService:getActiveEventMultiplier] Exception while processing events:', e);
    return 1; // Fallback em caso de exceção inesperada
  }
  // console.warn('[MissionService:getActiveEventMultiplier] Temporarily disabled: special_events table or columns not found in schema. Returning 1.');
  // return 1; // Retornar 1 temporariamente
}

// --- Funções CRUD para Special Events ---

/**
 * Lista todos os eventos especiais, com filtros opcionais.
 */
export async function listSpecialEvents(
  supabase: Supabase,
  filters: Partial<Pick<SpecialEventRow, 'event_type' | 'is_active'>> = {}
): Promise<SpecialEvent[]> {
  let query = supabase.from(SPECIAL_EVENTS_TABLE).select('*');

  if (filters.event_type) {
    query = query.eq('event_type', filters.event_type);
  }
  if (filters.is_active !== undefined) {
    query = query.eq('is_active', filters.is_active);
  }
  // Adicionar mais filtros se necessário (ex: por data)

  const { data, error } = await query.order('created_at', { ascending: false });

  if (error) {
    console.error('[MissionService:listSpecialEvents] Error fetching special events:', error);
    return [];
  }
  return data ? data.map(event => ({ 
    ...event, 
    config: event.config as unknown as SpecialEventConfig 
  } as SpecialEvent)) : [];
}

/**
 * Busca um evento especial pelo seu ID.
 */
export async function getSpecialEventById(supabase: Supabase, eventId: string): Promise<SpecialEvent | null> {
  const { data, error } = await supabase
    .from(SPECIAL_EVENTS_TABLE)
    .select('*')
    .eq('id', eventId)
    .maybeSingle();

  if (error) {
    console.error('[MissionService:getSpecialEventById] Error fetching special event by ID:', error);
    return null;
  }
  return data ? { 
    ...data, 
    config: data.config as unknown as SpecialEventConfig 
  } as SpecialEvent : null;
}

/**
 * Cria um novo evento especial.
 */
export async function createSpecialEvent(
  supabase: Supabase,
  eventData: Omit<SpecialEventRow, 'id' | 'created_at' | 'updated_at'>
): Promise<SpecialEvent | null> {
  // Validação básica para o campo config, que é NOT NULL
  if (!eventData.config) {
    console.error('[MissionService:createSpecialEvent] O campo "config" é obrigatório.');
    // Lançar um erro ou retornar null com uma mensagem mais específica pode ser uma opção
    throw new Error('O campo "config" do evento é obrigatório.');
  }
  if (!eventData.start_date || !eventData.end_date) { 
    console.error('[MissionService:createSpecialEvent] Os campos "start_date" e "end_date" são obrigatórios.');
    throw new Error('Os campos "start_date" e "end_date" do evento são obrigatórios.');
  }


  const { data, error } = await supabase
    .from(SPECIAL_EVENTS_TABLE)
    .insert(eventData)
    .select()
    .single();

  if (error) {
    console.error('[MissionService:createSpecialEvent] Error creating special event:', error);
    return null;
  }
  return data ? { 
    ...data, 
    config: data.config as unknown as SpecialEventConfig 
  } as SpecialEvent : null;
}

/**
 * Atualiza um evento especial existente.
 */
export async function updateSpecialEvent(
  supabase: Supabase,
  eventId: string,
  eventData: Partial<Omit<SpecialEventRow, 'id' | 'created_at' | 'updated_at'>>
): Promise<SpecialEvent | null> {
  const { data, error } = await supabase
    .from(SPECIAL_EVENTS_TABLE)
    .update(eventData)
    .eq('id', eventId)
    .select()
    .single();

  if (error) {
    console.error('[MissionService:updateSpecialEvent] Error updating special event:', error);
    return null;
  }
  return data ? { 
    ...data, 
    config: data.config as unknown as SpecialEventConfig 
  } as SpecialEvent : null;
}

/**
 * Exclui um modelo de missão.
 */
export async function deleteMissionTemplate(
  supabase: Supabase,
  missionId: string
): Promise<{ success: boolean, error?: PostgrestError }> {
  const { error } = await supabase
    .from(MISSION_TABLE)
    .delete()
    .eq('id', missionId);

  if (error) {
    console.error('[MissionService:deleteMissionTemplate] Error deleting mission template:', error);
    return { success: false, error };
  }
  return { success: true };
}

/**
 * Exclui um evento especial.
 */
export async function deleteSpecialEvent(
  supabase: Supabase,
  eventId: string
): Promise<{ success: boolean, error?: PostgrestError }> {
  const { error } = await supabase
    .from(SPECIAL_EVENTS_TABLE)
    .delete()
    .eq('id', eventId);

  if (error) {
    console.error('[MissionService:deleteSpecialEvent] Error deleting special event:', error);
    return { success: false, error };
  }
  return { success: true };
}

/**
 * Atribui uma missão específica (modelo) a todos os usuários do sistema.
 * ATENÇÃO: Esta é uma operação em massa e pode ser demorada.
 */
export async function assignMissionToAllUsers(
  supabase: Supabase,
  missionId: string,
  expiresAt?: Date | null 
): Promise<{ successCount: number; failureCount: number; error?: string }> {
  console.log(`[MissionService:assignMissionToAllUsers] Iniciando atribuição da missão ${missionId} para todos os usuários.`);
  let successCount = 0;
  let failureCount = 0;

  try {
    // 1. Buscar todos os IDs de usuários da tabela 'profiles'
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('id');

    if (profilesError) {
      console.error('[MissionService:assignMissionToAllUsers] Erro ao buscar perfis de usuários:', profilesError);
      return { successCount: 0, failureCount: 0, error: profilesError.message };
    }

    if (!profiles || profiles.length === 0) {
      console.warn('[MissionService:assignMissionToAllUsers] Nenhum perfil de usuário encontrado para atribuir a missão.');
      return { successCount: 0, failureCount: 0 };
    }

    console.log(`[MissionService:assignMissionToAllUsers] Atribuindo missão para ${profiles.length} usuários...`);

    // 2. Iterar e atribuir a missão a cada usuário
    // Para grandes números de usuários, considerar processamento em lote (batching)
    for (let i = 0; i < profiles.length; i++) {
      const profile = profiles[i];
      if (profile.id) {
        // Adiciona um pequeno log a cada N usuários para dar feedback de progresso
        if ((i + 1) % 50 === 0) {
          console.log(`[MissionService:assignMissionToAllUsers] Progresso: ${i + 1}/${profiles.length} usuários processados.`);
        }
        const assignedMission = await assignMissionToUser(supabase, profile.id, missionId, expiresAt);
        if (assignedMission) {
          successCount++;
        } else {
          // assignMissionToUser já loga o erro específico, aqui só contabilizamos a falha
          failureCount++;
        }
      } else {
        failureCount++; // Perfil sem ID, improvável mas tratado
      }
    }

    console.log(
      `[MissionService:assignMissionToAllUsers] Atribuição finalizada para missão ${missionId}. ` +
      `Sucessos: ${successCount}, Falhas: ${failureCount}`
    );
    return { successCount, failureCount };

  } catch (error: any) {
    console.error('[MissionService:assignMissionToAllUsers] Erro inesperado durante a atribuição em massa:', error);
    return { successCount, failureCount, error: error.message || 'Erro desconhecido na atribuição em massa' };
  }
}

// --- FUNÇÃO PARA BUSCAR MISSÕES CONCLUÍDAS PELO USUÁRIO ---
export async function getUserCompletedMissions(
  supabase: Supabase,
  userId: string,
  limit: number = 10, // Adiciona um limite para paginação futura, se necessário
  offset: number = 0
): Promise<UserMission[]> {
  const { data, error } = await supabase
    .from('user_missions')
    .select(`
      *,
      mission:missions(*)
    `)
    .eq('user_id', userId)
    .eq('is_complete', true)
    .order('completed_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) {
    console.error('[MissionService:getUserCompletedMissions] Error fetching completed missions:', error);
    throw error;
  }

  return (data || []).map(um => ({
    ...um,
    mission: um.mission as unknown as Mission, // Cast para o tipo Mission completo
    progress: um.progress as unknown as UserMissionProgress // Cast para o tipo UserMissionProgress
  })) as UserMission[];
}

/**
 * Retorna todos os modelos de missão que estão ativos e publicamente atribuíveis.
 */
export async function getAllAvailableMissions(
  supabase: Supabase
): Promise<Mission[]> {
  const { data, error } = await supabase
    .from(MISSION_TABLE)
    .select("*, badge_reward:badges(*)")
    .eq('is_active', true)
    .eq('is_publicly_assignable', true);

  if (error) {
    console.error('[MissionService:getAllAvailableMissions] Error fetching available missions:', error);
    return [];
  }

  return data.map(mission => {
    const missionRow = mission as unknown as MissionRow;
    return {
      ...missionRow,
      required_actions: (missionRow.required_actions as unknown as MissionRequiredActions) || [],
      badge_reward: mission.badge_reward || undefined,
      // Se is_publicly_assignable for null (não deveria acontecer devido ao filtro .eq)
      // ou undefined, tratamos como true (default) ou false dependendo da intenção.
      // Como filtramos por true, podemos assumir que é true ou converter explicitamente.
      is_publicly_assignable: missionRow.is_publicly_assignable === null ? undefined : !!missionRow.is_publicly_assignable,
    } as Mission;
  });
}
 